import { PlusOutlined } from '@ant-design/icons';
import { Heading, ScrollArea, Tooltip } from '@mm/components';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import { getPlanInfoFromBillingType } from '@mm/state/helpers/billing-info-helpers';
import {
  BasicPage,
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import { EnhancedPlan } from '@mm/state/interfaces/subscription';
import {
  OpenBillingModalParams,
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  getPageBillingType,
  getPlan,
  selectPlans,
  selectPlansMapping,
} from '@mm/state/selectors/facebookPageSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { partial } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Checkbox } from 'antd';
import Spinner from '../Spinner';
import './FacebookPagesAccess.pcss';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { landmineNames } from '@mm/constants/landmine-names';

type Props = {
  children?: React.ReactNode;
  pages?: BasicPage[];
  selectedPages: BasicPage[];
  onPagesSelected: (pages: BasicPage[]) => void;
  disabled: boolean;
};

// tslint:disable-next-line: cyclomatic-complexity
const FacebookPageEntry = ({
  page,
  disabled,
  selected,
  onSelect,
  onAddSeatsClick,
  onUpgradeButtonClick,
  canSelectInformer,
}: {
  page: BasicPage;
  selected?: boolean;
  disabled?: boolean;
  onSelect: (page: BasicPage) => void;
  onAddSeatsClick: (page: BasicPage, plan?: EnhancedPlan) => void;
  onUpgradeButtonClick: (page: BasicPage, plan?: EnhancedPlan) => void;
  canSelectInformer: (id: number, canSelect: boolean) => void;
}) => {
  const { userSeats, userSeatsUsed } =
    page.subscription || page.redemption || {};
  const leftSeats = (userSeats || 0) - (userSeatsUsed || 0);
  const mappings = useSelector(selectPlansMapping);
  const plans = useSelector(selectPlans) || [];
  const plan = getPlan(page, plans);

  const pageBillingType = getPageBillingType(page, mappings, plan);
  const info = getPlanInfoFromBillingType(pageBillingType, page);

  const planWithPageBillingType = plan
    ? {
        ...plan,
        pageBillingType,
      }
    : plan;

  const isFreeTrial = page?.redemption?.freeTrial;

  const unlimitedSeats =
    (info.isPromoPlan && !isFreeTrial) ||
    info.isLegacyProPlan ||
    info.isLegacyTeamPlan ||
    (info.isCustomTeam && page.subscription?.userSeats === null);

  const hasFreeSeats = userSeats ? leftSeats > 0 : true;

  const shouldAddSeats =
    !unlimitedSeats &&
    !hasFreeSeats &&
    pageBillingType !== PageBillingType.WP &&
    !info.isPromoPlan;

  const isDebtor = userSeats ? leftSeats < 0 : false;

  let content: string | JSX.Element = '';

  if (!hasFreeSeats) {
    content = 'No seats left';
  }

  if (isDebtor) {
    content = (
      <Tooltip
        title="You have some legacy users on this account that causes it to exceed the
        seat limit on your subscription. In order to add another user please
        upgrade your subscription, buy additional seats, OR delete existing
        users to make room."
      >
        <div>{leftSeats} seats</div>
      </Tooltip>
    );
  }

  const canSelectPage = Boolean(!disabled && (unlimitedSeats || hasFreeSeats));

  useEffect(() => {
    canSelectInformer(page.id, canSelectPage);
  }, [page.id, canSelectPage]);

  return (
    <div
      className={classNames('facebook-pages-access__page', {
        'facebook-pages-access__page-disabled': !canSelectPage,
      })}
    >
      <Checkbox
        onChange={!canSelectPage ? undefined : partial(onSelect, page)}
        checked={!!selected}
        disabled={!canSelectPage}
        className="facebook-pages-access__checkbox"
      >
        {' '}
        <Heading
          size={6}
          isBold={false}
          className={classNames('facebook-pages-access__checkbox-lable-title', {
            'facebook-pages-access__checkbox-lable-title-disabled': disabled,
          })}
        >
          {page.name || 'N/A'}
        </Heading>
      </Checkbox>
      <div className="facebook-pages-access__actions">
        {!unlimitedSeats && (
          <span
            className={classNames('facebook-pages-access__status', {
              'facebook-pages-access__status--warn': !leftSeats || isDebtor,
            })}
          >
            {content}
          </span>
        )}
        {shouldAddSeats && (
          <span
            className="facebook-pages-access__action"
            onClick={partial(onAddSeatsClick, page, planWithPageBillingType)}
          >
            <PlusOutlined />
            Seats
          </span>
        )}
      </div>
    </div>
  );
};

export const FacebookPagesAccess = (props: Props) => {
  const { disabled, onPagesSelected, selectedPages, pages } = props;
  const companyId = useSelector(selectActiveCompanyId);
  const appNavigationHistory = useSelector(
    (state: StoreState) => state.view.appNavigationHistory,
  );

  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();
  const modalActions = useModalActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const [isReady, setIsReady] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const selectableIdsRef = useRef(new Set());
  const isDisabled = disabled || pages?.every(p => p.disabled);

  const canSelectInformer = useCallback((id, canSelect) => {
    if (canSelect) {
      return selectableIdsRef.current.add(id);
    }
    return selectableIdsRef.current.delete(id);
  }, []);

  const onSelectAll = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked;
    const selectablePages =
      pages?.filter(page => selectableIdsRef.current?.has(page.id)) || [];

    if (!disabled) {
      const up = isChecked && pages ? [...selectablePages] : [];
      onPagesSelected(up);
    }
  };

  const onSelect = (page: BasicPage) => {
    const match = selectedPages.find(v => v.id === page.id);

    if (!match) {
      return onPagesSelected([...selectedPages, page]);
    }

    const up = selectedPages.filter(v => v.id !== page.id);
    return onPagesSelected(up);
  };

  const showUpgradeModal = () => {
    modalActions.openNewBillingModal2023({
      landmineName: landmineNames.INVITE_USER_MODAL,
    });
  };

  const openBillingModal = async (
    page: BasicPage,
    plan?: EnhancedPlan,
    seatsObject?: OpenBillingModalParams['seatsObject'],
  ) => {
    if (plan) {
      const res = await landmineOpensCRUDActions.create({
        landmineName: landmineNames.INVITE_USER_MODAL,
        navigationHistory: appNavigationHistory,
        planName: plan.shortName,
        billingOpened: false,
      });

      modalActions.openBillingModal({
        facebookPage: page as EnhancedBillingPage,
        plan,
        seatsObject,
        landmineOpenId: res?.id,
        stayOnPage: true,
        subscription: page.subscription,
        onSuccessBilling: () => {
          selectableIdsRef.current.clear();
        },
      });
    } else {
      showUpgradeModal();
    }
  };

  const onAddSeats = (page: BasicPage, plan?: EnhancedPlan) => {
    const seatsObject = {
      userSeats: page.subscription?.userSeats || 0,
      extraUserSeats: page.subscription?.extraUserSeats || 0,
    };

    openBillingModal(page, plan, seatsObject);
  };
  const isSelected = id =>
    !!selectedPages.find(selectedPage => selectedPage.id === id);

  useEffect(() => {
    if (
      selectedPages.length === pages?.length ||
      (selectableIdsRef.current.size > 0 &&
        selectedPages.length === selectableIdsRef.current.size)
    ) {
      setSelectAll(true);
      setIndeterminate(false);
    } else {
      setSelectAll(false);
      setIndeterminate(!!selectedPages.length);
    }
  }, [selectedPages.length, canSelectInformer, pages?.length]);

  useEffect(() => {
    (async () => {
      if (companyId) {
        setIsReady(false);
        await connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
          withSubscription: true,
        });
        setIsReady(true);
      }
    })();
  }, [companyId]);

  if (!isReady) {
    return (
      <Spinner
        className="invite-user-modal__spinner"
        backgroundColor="transparent"
        position="relative"
        size={24}
        show
      />
    );
  }

  return (
    <div
      className={classNames('facebook-pages-access', {
        'facebook-pages-access--disabled': isDisabled,
      })}
    >
      <div className="facebook-pages-access__header">
        <Heading size={5} isBold>
          Pages
        </Heading>
      </div>

      <ScrollArea className="facebook-pages-access__pages">
        <Checkbox
          indeterminate={indeterminate}
          onChange={onSelectAll}
          checked={selectAll}
          disabled={isDisabled}
          className={classNames(
            'facebook-pages-access__checkbox',
            'facebook-pages-access__checkbox-select-all',
            {
              'facebook-pages-access__page-disabled': isDisabled,
            },
          )}
        >
          All Accounts
        </Checkbox>
        {pages?.map(page => (
          <FacebookPageEntry
            key={page.id}
            disabled={disabled || page.disabled}
            page={page}
            selected={isSelected(page.id)}
            onSelect={onSelect}
            onAddSeatsClick={onAddSeats}
            onUpgradeButtonClick={() => showUpgradeModal()}
            canSelectInformer={canSelectInformer}
          />
        ))}
      </ScrollArea>
    </div>
  );
};
